import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FormBuilder,
  buildFormValuesFromFields,
  buildPayloadFromFields,
  getResolver,
} from '@folio/components';
import { useQuery } from '@tanstack/react-query';
import {
  brokerageFeeControllerGetBrokerFeesQueryOptions,
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  keyValueFieldsToObject,
  useBrokerageFeeControllerCreateBrokerageFee,
  useBrokerageFeeControllerUpdateBrokerageFee,
  useBrokerageFirmControllerUpdateBrokerageFirm,
} from 'korint-sdk';
import type { IntermediaryAdditionalInformationField } from 'korint-sdk/react';
import { PencilIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface IntermediaryAdditionalInfoDialogueCardModuleProps {
  /**
   * The brokerage firm id.
   */
  brokerageFirmId: string;

  /**
   * The product id.
   */
  productId: string;

  /**
   * The fields of the form.
   */
  fields: IntermediaryAdditionalInformationField[];
}

export function IntermediaryAdditionalInfoDialogueCardModule({
  brokerageFirmId,
  productId,
  fields,
}: IntermediaryAdditionalInfoDialogueCardModuleProps) {
  const { t } = useTranslation();

  const {
    data: brokerageFees,
    status: getBrokerageFeesStatus,
    refetch: refetchBrokerageFees,
  } = useQuery({
    ...brokerageFeeControllerGetBrokerFeesQueryOptions({ brokerageFirmId }),
    select: data => ({
      ...data,
      fees: data.fees.map(fee => ({
        ...fee,
        minManagementFee: fee.minManagementFee != null ? fee.minManagementFee / 100 : undefined,
        maxManagementFee: fee.maxManagementFee != null ? fee.maxManagementFee / 100 : undefined,
      })),
    }),
  });

  const { data: brokerageFirm, refetch: refetchBrokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const brokerageFee = brokerageFees?.fees.find(fee => fee.productId === productId);

  const getMergedFormData = () => {
    const mergedData = {
      ...(brokerageFee || {}),
      customFields: {
        ...brokerageFirm?.customFields,
      },
    };

    return buildFormValuesFromFields(fields, mergedData);
  };

  const form = useForm({
    values: getMergedFormData(),
    resolver: getResolver(fields),
  });

  const [open, setOpen] = useState(false);

  const { mutateAsync: createBrokerageFee } = useBrokerageFeeControllerCreateBrokerageFee({
    mutation: {
      onSuccess: () => {
        refetchBrokerageFees();
        form.reset(getMergedFormData());
      },
    },
  });
  const { mutateAsync: updateBrokerageFee } = useBrokerageFeeControllerUpdateBrokerageFee({
    mutation: {
      onSuccess: () => {
        refetchBrokerageFees();
        form.reset(getMergedFormData());
      },
    },
  });

  const { mutateAsync: updateIntermediaryAdditionalInformation } =
    useBrokerageFirmControllerUpdateBrokerageFirm({
      mutation: {
        onSuccess: () => {
          refetchBrokerageFirm();
          form.reset(getMergedFormData());
        },
      },
    });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          name="edit-brokerage-fee"
          aria-label={t('distributionRequestList.actions.edit')}
        >
          <PencilIcon className="h-4 w-4 text-black-800" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('distributionRequestList.brokerageFeeDialogue.title')}</DialogTitle>
        </DialogHeader>
        <FormBuilder fields={fields} form={form} isLoading={getBrokerageFeesStatus === 'pending'} />
        <DialogFooter>
          <Button
            type="submit"
            loading={form.formState.isSubmitting}
            form="termination-form"
            onClick={form.handleSubmit(async values => {
              const standardFeeFields = fields.filter(field => !('isCustomField' in field));
              const customFields = fields.filter(
                field => 'isCustomField' in field && field.isCustomField,
              );

              const brokerageFeeData = buildPayloadFromFields(standardFeeFields, values);

              const customFieldsPayload = buildPayloadFromFields(customFields, values);

              if (Object.keys(brokerageFeeData).length > 0) {
                if (brokerageFeeData.minManagementFee) {
                  brokerageFeeData.minManagementFee =
                    Number(brokerageFeeData.minManagementFee) * 100;
                }

                if (brokerageFeeData.maxManagementFee) {
                  brokerageFeeData.maxManagementFee =
                    Number(brokerageFeeData.maxManagementFee) * 100;
                }

                if (
                  brokerageFeeData.minCommission !== undefined &&
                  brokerageFeeData.minCommission !== null
                ) {
                  brokerageFeeData.defaultCommission = brokerageFeeData.minCommission;
                }

                if (brokerageFee) {
                  await updateBrokerageFee({
                    brokerageFeeId: brokerageFee.id,
                    data: brokerageFeeData,
                  });
                } else {
                  await createBrokerageFee({
                    brokerageFirmId,
                    data: {
                      productId,
                      ...brokerageFeeData,
                    },
                  });
                }
              }

              if (customFieldsPayload.customFields) {
                await updateIntermediaryAdditionalInformation({
                  brokerageFirmId,
                  data: customFieldsPayload,
                });
              }

              setOpen(false);
            })}
          >
            {t('distributionRequestList.brokerageFeeDialogue.validate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
